import React, { useMemo, useState, useEffect } from 'react';
import {
  Text,
  Button,
  Checkbox,
  Link as FluentLink,
  Badge,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkCircle16Regular } from '@fluentui/react-icons';
import BaseDialog from './BaseDialog';
import type { OrderLineRow } from './orderForm/ProductsSection';

const useStyles = makeStyles({
  subhead: {
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
    marginTop: '2px',
  },
  selectionBar: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    ...shorthands.padding('12px', '0'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    fontSize: '14px',
  },
  selectionDivider: {
    color: tokens.colorNeutralForeground4,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    ...shorthands.padding('12px', '0'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  rowDisabled: {
    opacity: 0.6,
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
    marginTop: '4px',
    fontSize: '12px',
    fontFamily: 'monospace',
    color: tokens.colorBrandForeground1,
    ...shorthands.padding('3px', '8px'),
    ...shorthands.borderRadius('999px'),
    ...shorthands.border('1px', 'solid', tokens.colorBrandStroke1),
    lineHeight: '16px',
  },
  pillGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    ...shorthands.gap('6px'),
  },
  count: {
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  empty: {
    ...shorthands.padding('24px', '0'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

export interface StartItemsConfirm {
  /** Booqable line ids the user chose to start. */
  selectedLineIds: string[];
  /** True when the selection covers every still-fulfillable line — the caller
   *  then omits line_ids and uses the all-items start path. */
  isAllRemaining: boolean;
}

interface StartItemsDialogProps {
  open: boolean;
  /** The order's lines (as rendered in ProductsSection). */
  lines: OrderLineRow[];
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (result: StartItemsConfirm) => void;
}

// Lines that can be fulfilled: real product lines (custom/fee lines have no
// planning) that aren't already out or returned.
const isFulfillable = (l: OrderLineRow): boolean =>
  l.kind !== 'custom' &&
  l.kind !== 'convenience_fee' &&
  !l.started &&
  !l.stopped &&
  !l.pending;

/**
 * Start dialog — pick which of the order's items are going out. Per-line
 * selection (all units of a line start together). Already-out lines are shown
 * for context but can't be re-selected. Selecting everything that's left routes
 * the caller to the all-items start; a strict subset is a partial start.
 */
export const StartItemsDialog: React.FC<StartItemsDialogProps> = ({
  open,
  lines,
  busy = false,
  onCancel,
  onConfirm,
}) => {
  const styles = useStyles();

  const productLines = useMemo(
    () => lines.filter((l) => l.kind !== 'custom' && l.kind !== 'convenience_fee' && !l.pending),
    [lines],
  );
  const selectable = useMemo(() => productLines.filter(isFulfillable), [productLines]);

  // Default: everything still out-able is checked.
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!open) return;
    const next: Record<string, boolean> = {};
    selectable.forEach((l) => {
      next[l.id] = true;
    });
    setSelected(next);
  }, [open, selectable]);

  const selectedIds = selectable.filter((l) => selected[l.id]).map((l) => l.id);
  const selectedCount = selectedIds.length;
  const total = selectable.length;
  const isAllRemaining = selectedCount === total;

  const selectAll = () => {
    const next: Record<string, boolean> = {};
    selectable.forEach((l) => {
      next[l.id] = true;
    });
    setSelected(next);
  };
  const clearSelection = () => setSelected({});

  return (
    <BaseDialog
      isOpen={open}
      onClose={onCancel}
      maxWidth="640px"
      showCloseButton
      title={
        <div>
          <Text weight="semibold" style={{ fontSize: '20px', display: 'block' }}>
            Start
          </Text>
          <Text className={styles.subhead}>Select items that are going out</Text>
        </div>
      }
      actions={
        <>
          <Button appearance="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            disabled={busy || selectedCount === 0}
            onClick={() => onConfirm({ selectedLineIds: selectedIds, isAllRemaining })}
          >
            {busy ? 'Starting…' : `Start ${selectedCount}/${total} items`}
          </Button>
        </>
      }
    >
      {total === 0 ? (
        <div className={styles.empty}>
          <Text>All items on this order are already out.</Text>
        </div>
      ) : (
        <>
          <div className={styles.selectionBar}>
            <FluentLink as="button" onClick={selectAll} disabled={busy || selectedCount === total}>
              Select all
            </FluentLink>
            <span className={styles.selectionDivider}>|</span>
            <FluentLink as="button" onClick={clearSelection} disabled={busy || selectedCount === 0}>
              Clear selection
            </FluentLink>
          </div>

          {productLines.map((line) => {
            const out = !isFulfillable(line);
            const units = line.stockUnits || [];
            return (
              <div
                key={line.id}
                className={`${styles.row} ${out ? styles.rowDisabled : ''}`}
              >
                <Checkbox
                  checked={out ? false : !!selected[line.id]}
                  disabled={out || busy}
                  onChange={(_, data) =>
                    setSelected((prev) => ({ ...prev, [line.id]: !!data.checked }))
                  }
                  aria-label={`Start ${line.name}`}
                />
                <div className={styles.rowBody}>
                  <Text weight="semibold" style={{ display: 'block' }}>
                    {line.name}
                  </Text>
                  {units.length > 0 ? (
                    <div className={styles.pillGroup}>
                      {units.map((u) => (
                        <span key={u.id} className={styles.pill}>
                          {u.identifier}
                          <CheckmarkCircle16Regular />
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
                {out ? (
                  <Badge appearance="tint" color="informative">
                    Out
                  </Badge>
                ) : (
                  <Text className={styles.count}>
                    {line.quantity}/{line.quantity}
                  </Text>
                )}
              </div>
            );
          })}
        </>
      )}
    </BaseDialog>
  );
};

export default StartItemsDialog;
